import React from "react";
import MDButton from "components/MDButton";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { BulkResetOutcome, BulkResetSummary } from "./bulkEventReset";
import { eventResetsLink } from "./eventResets";

const OUTCOME_LABEL: Record<BulkResetOutcome, string> = {
  succeeded: "Succeeded",
  warning: "Check trip",
  failed: "Failed",
  skipped: "Skipped",
};

const OUTCOME_COLOR: Record<BulkResetOutcome, string> = {
  succeeded: "green",
  warning: "#ed6c02",
  failed: "#d32f2f",
  skipped: "#616161",
};

interface Props {
  summary: BulkResetSummary | null;
  onClose: () => void;
}

// Per-trip outcome of a bulk Event Reset. A dialog rather than toasts: a 50-trip run needs a
// list the reviewer can read down and act on, and it must stay up until they dismiss it.
const BulkEventResetResultsDialog = ({ summary, onClose }: Props) => (
  <Dialog open={summary !== null} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>Reset events results</DialogTitle>
    {summary && (
      <DialogContent dividers>
        {summary.error && (
          <Typography variant="body2" sx={{ mb: 1, color: OUTCOME_COLOR.failed }}>
            The run stopped early: {summary.error}
          </Typography>
        )}
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {summary.succeeded} succeeded, {summary.warnings} need checking, {summary.failed} failed, {summary.skipped}{" "}
          skipped. Successful trips were run through the auto-approver; its verdict is in the details. Trips marked
          Check trip were (or may have been) changed but did not finish cleanly; failed and skipped trips were not
          changed.
        </Typography>
        {summary.batchId && (
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            <Link to={eventResetsLink(summary.batchId)} target="_blank" rel="noopener noreferrer">
              Open this run in Event Resets history
            </Link>
          </Typography>
        )}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 100 }}>Trip #</TableCell>
              <TableCell sx={{ width: 90 }}>Result</TableCell>
              <TableCell>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {summary.rows.map((r) => (
              <TableRow key={r.tripId} data-testid={`bulk-reset-row-${r.tripId}`}>
                <TableCell>
                  <Link to={`/trips/${r.tripId}?tab=events`} target="_blank" rel="noopener noreferrer">
                    {r.tripId}
                  </Link>
                </TableCell>
                <TableCell sx={{ color: OUTCOME_COLOR[r.outcome], fontWeight: 500, whiteSpace: "nowrap" }}>
                  {OUTCOME_LABEL[r.outcome]}
                </TableCell>
                <TableCell>{r.detail}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
    )}
    <DialogActions>
      <MDButton variant="contained" color="info" size="small" onClick={onClose}>Close</MDButton>
    </DialogActions>
  </Dialog>
);

export default BulkEventResetResultsDialog;
